import httpClient from '../core/httpClient';

const USER_PROFILE_CACHE_KEY = 'user.profile.cache';

// In-memory deduplikacja jednoczesnych wywołań `/users/me`. Trzymana w
// module – pomiędzy wywołaniami komponentów ten sam Promise jest zwracany,
// więc równoległe `useEffect`-y w SideMenu i UserPanelPage nie wykonują
// duplikatów requestów.
let inflightProfilePromise = null;

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readCachedProfile() {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(USER_PROFILE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile) {
  const storage = getLocalStorage();
  if (!storage) return;
  try {
    if (profile == null) {
      storage.removeItem(USER_PROFILE_CACHE_KEY);
    } else {
      storage.setItem(USER_PROFILE_CACHE_KEY, JSON.stringify(profile));
    }
  } catch {
    // Ignorujemy błędy quota / private mode.
  }
}

export function clearCachedUserProfile() {
  writeCachedProfile(null);
  inflightProfilePromise = null;
}

function isTransientRequestError(error) {
  if (!error) return false;
  if (!error.response) return true;
  const status = error.response.status;
  return status >= 500 || status === 408 || status === 429;
}

async function withTransientRetry(fn, { retries = 1, delayMs = 600 } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < retries && isTransientRequestError(error)) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

async function fetchAndCacheCurrentUserProfile() {
  const response = await withTransientRetry(() => httpClient.get('/api/v2/users/me'));
  const profile = response.data;
  console.info('[usersApi] getCurrentUserProfile: pobrano profil użytkownika', {
    id: profile?.id,
    username: profile?.username,
  });
  writeCachedProfile(profile);
  return profile;
}

export async function getCurrentUserProfile({ force = false } = {}) {
  if (!force) {
    const cached = readCachedProfile();
    if (cached) return cached;
    if (inflightProfilePromise) return inflightProfilePromise;
  }

  const promise = fetchAndCacheCurrentUserProfile();
  if (!force) {
    const wrapped = promise.finally(() => {
      if (inflightProfilePromise === wrapped) inflightProfilePromise = null;
    });
    inflightProfilePromise = wrapped;
    return wrapped;
  }
  return promise;
}

export async function getUserStats({ page = 1, limit = 50 } = {}) {
  const response = await httpClient.get('/api/v2/user-stats', {
    params: { page, limit },
  });
  const body = response.data;
  console.info('[usersApi] getUserStats: pobrano statystyki użytkownika', {
    total: body?.meta?.total,
    page: body?.meta?.page,
    count: body?.data?.length,
  });
  return body;
}

export async function updateCurrentUserProfile({
  username,
  email,
  phone_number,
  country,
  birth_date,
}) {
  const payload = {};
  if (username !== undefined) payload.username = username;
  if (email !== undefined) payload.email = email;
  if (phone_number !== undefined) payload.phone_number = phone_number;
  if (country !== undefined) payload.country = country;
  if (birth_date !== undefined) payload.birth_date = birth_date;

  const response = await httpClient.put('/api/v2/users/me', payload);
  const body = response.data;
  console.info('[usersApi] updateCurrentUserProfile: profil zaktualizowany', {
    id: body?.id,
    pola: Object.keys(payload),
  });
  writeCachedProfile(body);
  return body;
}

export async function changeCurrentUserPassword({ current_password, new_password }) {
  const response = await httpClient.put('/api/v2/users/me/password', {
    current_password,
    new_password,
  });
  const body = response.data;
  console.info('[usersApi] changeCurrentUserPassword:', body?.message);
  return body;
}

export async function deleteCurrentUserAccount({ password } = {}) {
  const response = await httpClient.delete('/api/v2/users/me', { data: { password } });
  const body = response.data;
  console.info('[usersApi] deleteCurrentUserAccount:', body?.message);
  clearCachedUserProfile();
  return body;
}
